import _styled, { default as _styled2, default as _styled3 } from "styled-components";
const _excluded = ["style", "className", "children", "active", "initialTab", "canClickSelectedTab", "onChange", "labelWidth", "renderExtraItems"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Animation from '@adsk/alloy-react-animation';
import { useMeasure, getValidChildren } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import TabTitle from './TabTitle';
const isActive = (selected, child) => child.props.tab === selected;
const firstTab = children => {
  const tabs = React.Children.toArray(children);
  if (!tabs.length) {
    return '';
  }
  return tabs[0].props.tab;
};
const LABEL_WIDTH = {
  AUTO: 'auto',
  FULL: 'full'
};
/**
 * Tabs keep related content in a single container that is shown and hidden through navigation.
 */
const Tabs = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      children,
      active,
      initialTab,
      canClickSelectedTab = true,
      onChange,
      labelWidth = LABEL_WIDTH.AUTO,
      renderExtraItems
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [activeTab, setActiveTab] = useState(initialTab || firstTab(children));
  const [focusedTab, setFocusedTab] = useState(null);
  const childRefs = useRef({});
  const tabListRef = useRef(null);
  const bounds = useMeasure(tabListRef);
  const [slider, setSlider] = useState({
    left: 0,
    width: 0
  });
  const finalActiveTab = active || activeTab;
  useEffect(() => {
    const target = childRefs.current[finalActiveTab];
    const container = tabListRef.current;
    if (target && container) {
      const cRect = container.getBoundingClientRect();
      if (cRect.width === 0) {
        return;
      }
      const tRect = target.getBoundingClientRect();
      const left = tRect.left - cRect.left;
      setSlider({
        hasValue: true,
        left,
        width: tRect.width
      });
    }
  }, [finalActiveTab, bounds]);
  const handleTabSelection = child => {
    const {
      tab,
      onClick
    } = child.props;
    !active && setActiveTab(tab);
    onChange && onChange(tab);
    onClick && onClick();
  };
  const blurFocusedTab = () => {
    if (!focusedTab) {
      return;
    }
    const focusedTabElement = childRefs.current[focusedTab];
    focusedTabElement && focusedTabElement.blur();
  };
  const handleOnClick = (e, child) => {
    // ctrl+click or cmd+click should have default behavior of anchor link
    if (e.ctrlKey || e.metaKey) {
      blurFocusedTab();
      return;
    }
    e.preventDefault();
    (canClickSelectedTab || finalActiveTab !== child.props.tab) && handleTabSelection(child);
    blurFocusedTab();
  };
  const handleOnFocus = child => setFocusedTab(child.props.tab);
  const handleOnBlur = () => setFocusedTab(null);
  const tabTitleStyle = labelWidth === LABEL_WIDTH.FULL ? {
    flex: 1
  } : undefined;
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({}, props, {
    className: className,
    ref: ref,
    $_css: [{
      display: 'block'
    }, style]
  }), /*#__PURE__*/React.createElement(_StyledDiv2, {
    ref: tabListRef,
    $_css2: [{
      position: 'relative',
      display: 'flex',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: theme.tokens.colors.border.subtle.value
    }]
  }, /*#__PURE__*/React.createElement(_StyledDiv3, {
    role: "tablist",
    $_css3: [{
      display: 'flex',
      flex: 1
    }]
  }, React.Children.map(getValidChildren(children), (child, key) => {
    var _child$props, _child$props$labelPro;
    return /*#__PURE__*/React.createElement(TabTitle, _extends({
      key: key,
      disabled: isActive(finalActiveTab, child) && !canClickSelectedTab || child.props.disabled,
      active: isActive(finalActiveTab, child),
      onClick: e => handleOnClick(e, child),
      onBlur: () => handleOnBlur(),
      onFocus: () => handleOnFocus(child),
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === 'Space') {
          e.preventDefault();
          e.stopPropagation();
          handleTabSelection(child);
        }
      },
      ref: el => {
        if (el) {
          childRefs.current[child.props.tab] = el;
        }
      },
      renderIcon: child.props.renderIcon
    }, child.props.labelProps, {
      style: [tabTitleStyle, (_child$props = child.props) === null || _child$props === void 0 ? void 0 : (_child$props$labelPro = _child$props.labelProps) === null || _child$props$labelPro === void 0 ? void 0 : _child$props$labelPro.style]
    }), child.props.label);
  })), renderExtraItems === null || renderExtraItems === void 0 ? void 0 : renderExtraItems(), slider.hasValue && /*#__PURE__*/React.createElement(Animation, {
    style: [{
      height: 2,
      bottom: -1,
      position: 'absolute',
      background: theme.tokens.colors.border.selected.value
    }, {
      left: slider.left,
      width: slider.width
    }],
    layout: true,
    initial: false
  })), /*#__PURE__*/React.createElement(Fragment, null, React.Children.map(getValidChildren(children), child => isActive(finalActiveTab, child) && child)));
});
Tabs.displayName = 'Tabs';
Tabs.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The component's children tabs */
  children: PropTypes.any,
  /** selected tab on init of tabs */
  initialTab: PropTypes.string,
  /** controlled selected tab */
  active: PropTypes.string,
  /** onChange callback which returns key */
  onChange: PropTypes.func,
  /** Whether selected tabs are clickable or not. Defaults to true */
  canClickSelectedTab: PropTypes.bool,
  /** Whether the tab label width should be based on text or take full width. */
  labelWidth: PropTypes.oneOf(Object.values(LABEL_WIDTH)),
  /** Extra items render function */
  renderExtraItems: PropTypes.func
};
export default Object.assign(Tabs, {
  LABEL_WIDTH
});
var _StyledDiv = _styled("div")`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div")`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  displayName: "Tabs___StyledDiv3",
  componentId: "sc-v38ayv-0"
})(["", ""], p => p.$_css3);
//# sourceMappingURL=Tabs.js.map